import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { apiService } from '../../services/api';
import websocketService from '../../services/websocketService';
import * as fmt from '../../lib/format';

interface SyncJob {
  id: number;
  job_type: string;
  mode: string | null;
  triggered_by_name: string | null;
  status: 'running' | 'succeeded' | 'failed';
  total: number | null;
  processed: number;
  error: string | null;
  started_at: string;
  updated_at: string;
  finished_at: string | null;
}

const JOB_LABELS: Record<string, string> = {
  users: 'Users',
  user_groups: 'User Groups',
  issues: 'Issues',
  worklogs: 'Worklogs',
  status_logs: 'Status Logs',
};

const STATUS_STYLES: Record<string, string> = {
  running: 'bg-amber-50 text-amber-700 ring-amber-200',
  succeeded: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  failed: 'bg-rose-50 text-rose-700 ring-rose-200',
};

function fmtDuration(startIso: string, endIso: string | null): string {
  if (!endIso) return '';
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (!isFinite(ms) || ms < 0) return '';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

export default function SyncJobsTable() {
  const [jobs, setJobs] = useState<SyncJob[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await apiService.getSyncJobs();
      if (res?.success && Array.isArray(res.data)) setJobs(res.data);
    } catch {
      /* ignore — table just stays empty */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    websocketService.connect();
    websocketService.onSyncJob((row: SyncJob) => {
      if (!row?.id) return;
      // Upsert by id, keep newest-first, cap at 50.
      setJobs((prev) => {
        const next = [row, ...prev.filter((j) => j.id !== row.id)];
        next.sort((a, b) => b.id - a.id);
        return next.slice(0, 50);
      });
    });
    return () => websocketService.offSyncJob();
  }, []);

  const rows = useMemo(() => jobs.slice().sort((a, b) => b.id - a.id), [jobs]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Sync jobs</h4>
          <p className="text-xs text-gray-500">Live status of data syncs (latest 50)</p>
        </div>
        <button
          onClick={load}
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
          title="Refresh" aria-label="Refresh"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="bg-gray-50/70 text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">
              <th className="px-4 py-2.5">Job</th>
              <th className="px-4 py-2.5">Triggered by</th>
              <th className="px-4 py-2.5">Started</th>
              <th className="px-4 py-2.5">Progress</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Completed</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center">
                  <RefreshCw className="mx-auto h-5 w-5 animate-spin text-gray-300" />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                  No sync jobs yet. Trigger a sync above to see it here.
                </td>
              </tr>
            ) : (
              rows.map((j) => {
                const label = JOB_LABELS[j.job_type] || j.job_type;
                const isRunning = j.status === 'running';
                return (
                  <tr key={j.id} className="align-middle">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{label}</div>
                      {j.mode && <div className="text-[0.6875rem] uppercase tracking-wide text-gray-500">{j.mode}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <div className="flex items-center gap-2">
                        <span>{j.triggered_by_name || fmt.MISSING}</span>
                        {j.triggered_by_name === 'scheduler' && (
                          <span
                            className="rounded-full bg-gray-100 px-2 py-0.5 text-[0.6875rem] uppercase tracking-wide text-gray-600"
                            title="Triggered by Cloud Scheduler"
                          >
                            scheduler
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">{fmt.dateTime(j.started_at)}</td>
                    <td className="px-4 py-3 min-w-[160px]">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                          {isRunning ? (
                            // While running the job may still be importing after fetch — show an
                            // active/indeterminate strip so a full bar never implies "done".
                            <div className="h-full w-1/3 rounded-full bg-blue-500 animate-pulse" />
                          ) : (
                            <div className={`h-full w-full rounded-full ${j.status === 'succeeded' ? 'bg-emerald-500' : 'bg-rose-400'}`} />
                          )}
                        </div>
                        <span className="whitespace-nowrap text-[0.6875rem] tabular-nums text-gray-500">
                          {fmt.count(j.processed)}{j.total ? ` / ${fmt.count(j.total)}` : ''}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${STATUS_STYLES[j.status] || ''}`}>
                        {isRunning && <RefreshCw className="h-3 w-3 animate-spin" />}
                        {j.status}
                      </span>
                      {j.error && <div className="mt-1 max-w-[200px] truncate text-[0.6875rem] text-rose-700" title={j.error}>{j.error}</div>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                      {j.finished_at ? (
                        <>
                          {fmt.dateTime(j.finished_at)}
                          <span className="ml-1 text-[0.6875rem] text-gray-500">({fmtDuration(j.started_at, j.finished_at)})</span>
                        </>
                      ) : fmt.MISSING}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
